<template>
  <div class="content">
    <div class="header d-flex align-items-center profile-header">
      <!-- Mask -->
      <span class="mask bg-gradient-info"></span>
      <!-- Header container -->
      <div class="container-fluid d-flex align-items-center">
        <div class="row">
          <div class="col-lg-12">
            <h1 class="display-4 text-white">{{ companyData.company_name || 'Loading...' }}</h1>
            <p class="text-white mt-0 mb-2" v-if="companyData.industry || companyData.primary_market">
              {{ companyData.industry || 'No industry specified' }}
              <span v-if="companyData.primary_market">• {{ companyData.primary_market }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-12">
          <h6 class="h2 text-white d-inline-block mb-0">
            Company Details
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb></route-breadcrumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <!-- Company Information -->
        <div class="col-lg-8">
          <card>
            <template slot="header">
              <div class="row align-items-center">
                <div class="col-8">
                  <h3 class="mb-0">Company Information</h3>
                </div>
                <div class="col-4 text-right">
                  <base-button 
                    type="primary" 
                    size="sm" 
                    @click="openEditModal"
                    class="mr-2"
                  >
                    <i class="fa fa-edit mr-1"></i> Edit
                  </base-button>
                  <base-button 
                    v-if="!hubspotConnected"
                    type="warning" 
                    size="sm" 
                    @click="syncToHubSpot"
                    :loading="syncingToHubSpot"
                  >
                    <i class="fab fa-hubspot mr-1"></i> Sync to HubSpot
                  </base-button>
                  <base-button 
                    v-if="hubspotConnected"
                    type="success" 
                    size="sm" 
                    @click="openAssociateLeadsModal"
                  >
                    <i class="fa fa-link mr-1"></i> Associate Leads
                  </base-button>
                </div>
              </div>
            </template>
            
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Company Name</label>
                  <p class="form-control-static">{{ companyData.company_name || 'Not specified' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Industry</label>
                  <p class="form-control-static">{{ companyData.industry || 'Not specified' }}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Primary Market</label>
                  <p class="form-control-static">{{ companyData.primary_market || 'Not specified' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">HubSpot Owner</label>
                  <p class="form-control-static">{{ hubspotOwnerName }}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">HubSpot Object ID</label>
                  <p class="form-control-static">{{ companyData.hubspot_object_id || 'Not assigned' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Total Leads</label>
                  <p class="form-control-static">
                    <span class="badge badge-primary">{{ companyData.leads_count || 0 }}</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Created</label>
                  <p class="form-control-static">{{ formatDate(companyData.created_at) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">Last Updated</label>
                  <p class="form-control-static">{{ formatDate(companyData.updated_at) }}</p>
                </div>
              </div>
            </div>
          </card>
        </div>

        <!-- Associated Leads -->
        <div class="col-lg-4">
          <company-leads-widget :company-id="companyId" />
        </div>
      </div>

      <!-- Associated Contacts Table -->
      <div class="row mt-4">
        <div class="col">
          <card>
            <template slot="header">
              <h3 class="mb-0">Associated Leads</h3>
            </template>
            
            <el-table
              v-loading="leadsLoading"
              class="table-responsive align-items-center table-flush table-striped"
              header-row-class-name="thead-light"
              :data="companyLeads"
              @row-click="selectLead"
            >
              <el-table-column label="Lead Name" min-width="200">
                <template v-slot="{ row }">
                  <router-link
                    class="font-weight-600 name-col"
                    :to="{ name: 'LeadDetails', params: { id: row.id } }"
                  >
                    {{ row.first_name }} {{ row.last_name }}
                  </router-link>
                </template>
              </el-table-column>
              <el-table-column label="Email" min-width="200" prop="email">
                <template v-slot="{ row }">
                  {{ row.email || 'Not provided' }}
                </template>
              </el-table-column>
              <el-table-column label="Phone" min-width="150" prop="phone">
                <template v-slot="{ row }">
                  {{ row.phone || 'Not provided' }}
                </template>
              </el-table-column>
              <el-table-column label="Country" min-width="100" align="center">
                <template v-slot="{ row }">
                  {{ row.country || 'N/A' }}
                </template>
              </el-table-column>
              <el-table-column label="Created" min-width="120" align="center">
                <template v-slot="{ row }">
                  {{ formatDate(row.created_at) }}
                </template>
              </el-table-column>
            </el-table>

            <div
              v-if="companyLeads.length === 0 && !leadsLoading"
              class="text-center py-4"
            >
              <p class="text-muted">No leads found for this company</p>
            </div>

            <div
              v-if="leadsPagination.total > leadsPagination.perPage"
              slot="footer"
              class="row align-items-center justify-content-between"
            >
              <div class="col">
                <p class="card-category">
                  Showing {{ leadsPagination.from }} to {{ leadsPagination.to }} of {{ leadsPagination.total }} leads
                </p>
              </div>
              <base-pagination
                class="pagination-no-border"
                v-model="leadsPagination.currentPage"
                :per-page="leadsPagination.perPage"
                :total="leadsPagination.total"
                @input="loadCompanyLeads"
              >
              </base-pagination>
            </div>
          </card>
        </div>
      </div>
    </div>

    <!-- Edit Company Modal -->
    <modal
      :show.sync="modalEditCompany"
      modal-classes="modal-dialog-centered modal-lg"
    >
      <template v-slot:header>
        <h5 class="modal-title">Edit Company</h5>
      </template>

      <div class="px-4 py-4">
        <form role="form" @submit.prevent="updateCompany">
          <div class="row">
            <div class="col-md-6">
              <base-input
                label="Company Name"
                v-model="editForm.company_name"
                placeholder="Company Name"
                required
              >
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input
                label="Industry"
              >
                <el-select
                  v-model="editForm.industry"
                  placeholder="Select Industry"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="(industry, index) in industries"
                    :key="`industry-${industry}-${index}`"
                    :label="industry"
                    :value="industry"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <base-input
                label="Primary Market"
              >
                <el-select
                  v-model="editForm.primary_market"
                  placeholder="Select Primary Market"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="(market, index) in primaryMarkets"
                    :key="`market-${market.value}-${index}`"
                    :label="market.label"
                    :value="market.value"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input
                label="HubSpot Owner"
              >
                <el-select
                  v-model="editForm.hubspot_owner_name"
                  placeholder="Select HubSpot Owner"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="(owner, index) in venueSpecialists"
                    :key="`owner-${owner.hubspot_owner_id}-${index}`"
                    :label="owner.name"
                    :value="owner.hubspot_owner_id"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <base-input
                label="HubSpot Object ID"
                v-model="editForm.hubspot_object_id"
                placeholder="HubSpot Object ID"
                :disabled="true"
                readonly
              >
              </base-input>
              <small class="form-text text-muted">This field cannot be edited</small>
            </div>
          </div>

          <div class="text-right mt-4">
            <base-button
              type="primary"
              :loading="updating"
              @click="updateCompany"
            >
              Save
            </base-button>
          </div>
        </form>
      </div>
    </modal>

    <!-- Associate Leads Modal Component -->
    <associate-leads-modal
      :show="modalAssociateLeads"
      :company-id="companyId"
      :company-name="companyData.company_name || 'Company'"
      @close="modalAssociateLeads = false"
      @leads-updated="onLeadsUpdated"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { Table, TableColumn, Select, Option, Notification } from "element-ui";
import RouteBreadcrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import CompanyLeadsWidget from "@/views/Widgets/Companies/CompanyLeadsWidget";
import AssociateLeadsModal from "@/components/Companies/AssociateLeadsModal";
import CompanyService from "@/services/company.service";
import moment from 'moment';

export default {
  components: {
    RouteBreadcrumb,
    BasePagination,
    CompanyLeadsWidget,
    AssociateLeadsModal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      companyId: parseInt(this.$route.params.id),
      companyLeads: [],
      leadsLoading: false,
      modalEditCompany: false,
      updating: false,
      editForm: {
        company_name: '',
        industry: '',
        primary_market: '',
        hubspot_owner_name: '', // Changed from hubspot_owner_id
        hubspot_object_id: ''
      },
      leadsPagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
        from: 0,
        to: 0
      },
      industries: [],
      primaryMarkets: [],
      // Associate Leads modal state
      modalAssociateLeads: false,
      // HubSpot sync state
      syncingToHubSpot: false
    };
  },
  computed: {
    ...mapGetters("companies/", {
      company: "getSingleCompanyById",
      getCompanyLeadsByCompanyId: "getCompanyLeadsByCompanyId",
      getCompanyLeadsTotalByCompanyId: "getCompanyLeadsTotalByCompanyId"
    }),
    ...mapState("companies/", {
      companiesIndustries: (state) => state.industries
    }),
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
    companyData() {
      return this.company(this.companyId);
    },
    // Use venue specialists data like in Leads.vue
    venueSpecialists() {
      let vcs = this.$store.state.venues.venueSpecialists;
      let data = [];
      for (let vc in vcs) {
        vc = vcs[vc];
        if(vc.hubspot_owner_id && vc.country.toUpperCase() === this.country) {
          data.push({
            label: vc.name,
            value: vc.hubspot_owner_id,
            hubspot_owner_id: vc.hubspot_owner_id,
            name: vc.name,
            active: vc.active,
            disabled: vc.disabled,
          });
        }
      }
      data.sort(function(a,b){
        a = a['label'];
        b = b['label'];
        return (a === b) ? 0 : (a < b) ? -1 : 1;
      });
      return data;
    },
    hubspotOwnerName() {
      const owner = this.venueSpecialists.find(o => o.hubspot_owner_id === this.companyData.hubspot_owner_id);
      return owner ? owner.name : 'Not assigned';
    },
    hubspotConnected() {
      return !!(this.companyData.hubspot_object_id && this.companyData.hubspot_owner_id);
    }
  },
  async created() {
    await Promise.all([
      this.loadCompany(),
      this.loadDropdownData(),
      this.$store.dispatch('venues/getVenueSpecialists') // Load venue specialists like in Leads.vue
    ]);
    this.populateEditForm();
    await this.loadCompanyLeads();
  },
  methods: {
    async loadCompany() {
      try {
        await this.$store.dispatch("companies/getSingle", { id: this.companyId });
      } catch (error) {
        Notification.error({
          message: 'Failed to load company details'
        });
      }
    },
    async loadDropdownData() {
      try {
        // Load industries
        await this.$store.dispatch("companies/getIndustries");
        this.industries = this.companiesIndustries || [];
        
        // Load primary markets
        const marketsResponse = await this.$store.dispatch("companies/getPrimaryMarkets");
        this.primaryMarkets = marketsResponse || [];
        
        // HubSpot owners will be loaded from venue specialists in created()
        // We'll populate this.hubspotOwners in the computed property
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
      }
    },
    async loadCompanyLeads(page = 1) {
      try {
        this.leadsLoading = true;
        await this.$store.dispatch("companies/getCompanyLeads", {
          companyId: this.companyId,
          page: page
        });
        
        this.companyLeads = this.getCompanyLeadsByCompanyId(this.companyId);
        const total = this.getCompanyLeadsTotalByCompanyId(this.companyId);
        
        this.leadsPagination.total = total;
        this.leadsPagination.currentPage = page;
        this.leadsPagination.from = ((page - 1) * this.leadsPagination.perPage) + 1;
        this.leadsPagination.to = Math.min(page * this.leadsPagination.perPage, total);
        
      } catch (error) {
        Notification.error({
          message: 'Failed to load company leads'
        });
      } finally {
        this.leadsLoading = false;
      }
    },
    selectLead(lead) {
      this.$router.push({
        name: "LeadDetails",
        params: { id: lead.id },
      });
    },
    populateEditForm() {
      const owner = this.venueSpecialists.find(o => o.hubspot_owner_id === this.companyData.hubspot_owner_id);
      
      this.editForm = {
        company_name: this.companyData.company_name || '',
        industry: this.companyData.industry || '',
        primary_market: this.companyData.primary_market || '',
        hubspot_owner_name: owner ? owner.hubspot_owner_id : '', // Store the ID for submission
        hubspot_object_id: this.companyData.hubspot_object_id || ''
      };
    },
    async updateCompany() {
      try {
        this.updating = true;
        
        // Prepare data for API call
        const updateData = {
          id: this.companyId,
          company_name: this.editForm.company_name,
          industry: this.editForm.industry,
          primary_market: this.editForm.primary_market,
          hubspot_owner_id: this.editForm.hubspot_owner_name, // Send the ID
          hubspot_object_id: this.editForm.hubspot_object_id // This won't be sent since it's readonly
        };
        
        await this.$store.dispatch('companies/updateCompany', updateData);
        
        this.modalEditCompany = false;
        await this.loadCompany(); // Refresh company data
        this.populateEditForm(); // Update form with new data
        
        Notification.success({
          message: 'Company updated successfully!'
        });
      } catch (error) {
        Notification.error({
          message: 'Failed to update company'
        });
      } finally {
        this.updating = false;
      }
    },
    formatDate(date) {
      return date ? moment(date).format('MMM DD, YYYY') : 'N/A';
    },
    openEditModal() {
      this.modalEditCompany = true;
      this.populateEditForm();
    },
    openAssociateLeadsModal() {
      this.modalAssociateLeads = true;
    },
    onLeadsUpdated() {
      // Refresh company data when leads are updated
      this.loadCompany();
      this.loadCompanyLeads(this.leadsPagination.currentPage);
    },
    async syncToHubSpot() {
      try {
        this.syncingToHubSpot = true;
        
        // Call the HubSpot sync service directly
        await CompanyService.syncToHubSpot(this.companyId);
        
        // Refresh company data to get updated HubSpot info
        await this.loadCompany();
        
        Notification.success({
          message: 'Company sync has been queued for HubSpot. The company and all associated leads/events will be synchronized shortly.'
        });
      } catch (error) {
        console.error('Failed to sync company to HubSpot:', error);
        Notification.error({
          message: 'Failed to sync company to HubSpot. Please try again.'
        });
      } finally {
        this.syncingToHubSpot = false;
      }
    }
  },
  watch: {
    companyData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.populateEditForm();
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.form-control-static {
  padding-top: 7px;
  padding-bottom: 7px;
  margin-bottom: 0;
  min-height: 34px;
}

.name-col {
  color: #5e72e4;
  font-size: 0.875rem;
}

.name-col:hover {
  color: #233dd2;
  text-decoration: underline;
}

.profile-header {
  min-height: 50px;
  position: relative;
  background: linear-gradient(87deg, #5e72e4 0, #825ee4 100%);
}

.associated-leads-list {
  max-height: 300px;
  overflow-y: auto;
}

.associated-lead-item {
  background-color: #f8f9fa;
}
</style>